import { Link, useLocation } from 'react-router-dom'
import MuiBreadcrumbs from '@mui/material/Breadcrumbs'
import { Typography } from '@mui/material'
import { IconChevronRight } from '@tabler/icons'
import { useMemo } from 'react'

import useBreadcrumbs from './useBreadcrumbs'

const getBreadcrumbLabel = (path: string, fullPath: string) => {
  const routeLabels = {
    teammates: 'Teammates',
    templates: 'Templates',
    team: 'Team',
    'user-tools': 'Tools',
    apikeys: 'Keys & Secrets',
    teams: 'Teams',
    'shared-teams': 'Shared Teams',
    playground: 'Playground',
  }

  const selectedNodeLabel = {
    teammates: 'Teammate',
    workflows: 'Workflow',
    team: 'Team',
    'user-tools': 'Tool',
    skills: 'Skill',
  }

  const newLabels = {
    teammates: 'New Teammate',
    team: 'New Team',
    'user-tools': 'New Tool',
    skills: 'New Skill',
  }

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
  const pathParts = fullPath.split('/').filter(x => x)
  const originalPath = pathParts[0]

  if (uuidRegex.test(path) && selectedNodeLabel[originalPath]) {
    return selectedNodeLabel[originalPath]
  } else if (path === 'new') {
    return newLabels[originalPath] || 'New'
  }

  return routeLabels[path] || path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ')
}

export default function Breadcrumbs() {
  const { crumbs } = useBreadcrumbs()
  const location = useLocation()
  const pathnames = location.pathname.split('/').filter(x => x)

  const breadcrumbs = useMemo(() => {
    // Special cases for teams/
    if (pathnames[0] === 'teams' && pathnames[2]) {
      const breadcrumbs = [{ label: 'Teams', path: '/teams' }]

      if (crumbs && Array.isArray(crumbs)) {
        breadcrumbs.push(...crumbs)
      }

      return breadcrumbs
    }

    // Special case for skills/<id> — use the skill name supplied by the editor
    if (pathnames[0] === 'skills' && pathnames[1] && pathnames[1] !== 'new' && crumbs && Array.isArray(crumbs)) {
      return [{ label: 'Skills', path: '/skills' }, ...crumbs]
    }

    // Special case for teammates/<id>(/dialog) — a playground teammate's page supplies crumbs whose
    // Teammates link deep-links back to the playground tab. Normal teammates leave crumbs unset.
    if (pathnames[0] === 'teammates' && pathnames[1] && crumbs && Array.isArray(crumbs)) {
      return crumbs
    }

    return pathnames.map((pathname, index) => ({
      label: getBreadcrumbLabel(pathname, location.pathname),
      path: `/${pathnames.slice(0, index + 1).join('/')}`,
    }))
  }, [crumbs, location.pathname, pathnames])

  return (
    <MuiBreadcrumbs
      separator={<IconChevronRight width="16" height="16" />}
      sx={{ color: theme => theme.typography.textColor.secondary }}
      key={location.pathname}
    >
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1

        return isLast ? (
          <Typography
            key={crumb.path}
            sx={{
              textDecoration: breadcrumbs.length === 1 ? 'underline' : undefined,
            }}
          >
            {crumb.label}
          </Typography>
        ) : (
          <Link to={crumb.path} key={crumb.path} style={{ color: 'inherit' }}>
            {crumb.label}
          </Link>
        )
      })}
    </MuiBreadcrumbs>
  )
}
